import { documentToPlainTextString } from '@contentful/rich-text-plain-text-renderer';
import { documentToReactComponents, Options } from '@contentful/rich-text-react-renderer';
import { BLOCKS, INLINES } from '@contentful/rich-text-types';
import BCImage from 'components/BcImage';
import { OutlineLinkButton } from 'components/Button';
import RecipesCarousel from 'components/carousel/RecipesCarousel';
import AlarmIcon from 'components/icons/AlarmIcon';
import PrintIcon from 'components/icons/PrintIcon';
import RegistrationIcon from 'components/icons/RegistrationIcon';
import Img from 'components/Img';
import Container from 'components/layout/Container';
import Description from 'components/ui/Description';
import FeaturePanel from 'components/ui/FeaturePanel';
import ProductPrice from 'components/ui/ProductPrice';
import SubscriptionBadge from 'components/ui/SubscriptionBadge';
import { PRODUCT_FALLBACK_IMAGE } from 'config/const';
import { PurchaseType } from 'lib/bigcommerce/product-options';
import { ProductData } from 'lib/bigcommerce/types';
import { FeaturePanelData, RecipeData } from 'lib/contentful/types';
import React from 'react';

const renderRecipeRichText = (
  json: any,
  links: RecipeData['method']['links'] | RecipeData['ingredients']['links']
) => {
  const options: Options = {
    renderText: (text: string) => {
      return text.split('\n').map((t, i) =>
        i > 0 ? (
          <React.Fragment key={i}>
            <br />
            {t}
          </React.Fragment>
        ) : (
          <React.Fragment key={i}>{t}</React.Fragment>
        )
      );
    },
    renderNode: {
      [BLOCKS.EMBEDDED_ENTRY]: (node: any, children: React.ReactNode) => {
        const entry = links.entries.block.find((x: any) => x.sys.id === node.data.target.sys.id);

        if (entry && entry.__typename === 'FeaturePanel') {
          return (
            <div>
              <div className="mt-12">
                <FeaturePanel {...(entry as FeaturePanelData)} />
              </div>
            </div>
          );
        }

        return null;
      },
      [BLOCKS.HEADING_2]: (node: any, children: React.ReactNode) => {
        if (documentToPlainTextString(node).trim() === '') return null;
        return <h2 className="text-3xl">{children}</h2>;
      },
      [BLOCKS.HEADING_3]: (node: any, children: React.ReactNode) => {
        if (documentToPlainTextString(node).trim() === '') return null;
        return <h3 className="text-2xl">{children}</h3>;
      },
      [BLOCKS.HEADING_4]: (node: any, children: React.ReactNode) => {
        if (documentToPlainTextString(node).trim() === '') return null;
        return <h4 className="text-xl">{children}</h4>;
      },
      [BLOCKS.HEADING_5]: (node: any, children: React.ReactNode) => {
        if (documentToPlainTextString(node).trim() === '') return null;
        return <h5 className="text-lg">{children}</h5>;
      },

      [BLOCKS.OL_LIST]: (node: any, children: React.ReactNode) => {
        if (documentToPlainTextString(node).trim() === '' && !children) return null;
        if (documentToPlainTextString(node).trim() === '') return children;
        return <ol className="!mt-2 list-decimal space-y-2 pl-5">{children}</ol>;
      },
      [BLOCKS.UL_LIST]: (node: any, children: React.ReactNode) => {
        if (documentToPlainTextString(node).trim() === '' && !children) return null;
        if (documentToPlainTextString(node).trim() === '') return children;
        return <ul className="!mt-2 list-disc space-y-2 pl-5">{children}</ul>;
      },
      [BLOCKS.PARAGRAPH]: (node: any, children: React.ReactNode) => {
        const doc = documentToPlainTextString(node).trim();

        if (doc === '') return <p className="">{children}</p>;
        if (!children) return;

        return <p className={``}>{children}</p>;
      },
      [INLINES.HYPERLINK]: ({ data }, children) => (
        <a className="underline" href={data.uri}>
          {children}
        </a>
      ),
    },
  };

  return <div className="space-y-6 sm:text-lg">{documentToReactComponents(json, options)}</div>;
};

const RelatedProduct: React.FC<ProductData> = (props) => {
  const {
    defaultImage,
    name,
    weightSummary,
    variants,
    currency,
    subscriptionEnabled,
    link,
    categories,
  } = props;
  const oneOffVariant = variants.find((x) => x.purchaseType.id === PurchaseType.OneOff.id);
  return (
    <div className="mb-18 relative w-full bg-sf-gold-100 text-sf-blue-700 sm:mb-32">
      {' '}
      <div className="aspect-h-1 aspect-w-1 sm:aspect-h-3 sm:aspect-w-4 md:hidden">
        {defaultImage ? (
          <BCImage {...defaultImage} layout={'fixed'} objectFit="cover" className="" />
        ) : (
          <Img url={PRODUCT_FALLBACK_IMAGE} layout="fill" objectFit={'cover'} />
        )}
      </div>
      <div className="relative flex items-center xl:min-h-[700px]">
        <div className="left-1/2 hidden h-full w-1/2 md:absolute md:block">
          {defaultImage ? (
            <BCImage {...defaultImage} layout={'fixed'} objectFit="cover" className="" />
          ) : (
            <div className="absolute inset-0">
              <Img
                url={PRODUCT_FALLBACK_IMAGE}
                layout="fill"
                objectFit="cover"
                objectPosition="center"
              />
            </div>
          )}
        </div>
        <Container className="md:px-12">
          <div className="px-6 py-16 md:w-1/2 lg:px-24">
            <div className="mb-5 text-lg font-medium uppercase">Perfect for this recipe</div>
            <div className="mb-4 text-sm font-medium uppercase">
              {categories.map((c) => c.name).join(' | ')}
            </div>
            <h2 className="mb-4 text-2xl uppercase">{name}</h2>
            <div className="mb-4 flex items-center gap-3 text-sm">
              <span className="font-medium">{weightSummary} </span>
              <span className="block self-stretch border-l border-sf-blue-700"></span>
              <span className="flex gap-2">
                <ProductPrice variant={oneOffVariant} currency={currency} />
              </span>
            </div>
            {subscriptionEnabled && (
              <div className="mb-5 flex">
                <SubscriptionBadge type="normal" />
              </div>
            )}
            <OutlineLinkButton internalUrl={link}>Shop Now</OutlineLinkButton>
          </div>
        </Container>
      </div>
    </div>
  );
};
type RecipeProps = RecipeData;

const Recipe: React.FC<RecipeProps> = (props) => {
  const {
    title,
    image,
    ingredients,
    description,
    method,
    relatedProduct,
    cookingTime,
    serves,
    moreRecipes,
    cookingMethod,
    seafoodType,
  } = props;

  return (
    <>
      <div className=" mb-8 rounded-b bg-sf-gold-100 sm:mb-14 lg:bg-transparent">
        <Container>
          <div className="grid grid-cols-4 rounded-b bg-sf-gold-100 lg:grid-cols-12 lg:rounded">
            <div className="col-span-4 -mx-6 md:col-span-6 lg:m-0">
              <div className="aspect-h-3 aspect-w-4 sm:aspect-h-2  lg:aspect-h-1 	lg:aspect-w-1">
                <Img
                  key={image.url}
                  {...image}
                  className="rounded-b lg:rounded"
                  layout="fill"
                  sizes="(max-width:1023px) 100vw, 700px"
                  objectFit="cover"
                />
              </div>
            </div>
            <div className="col-span-4 place-self-center py-20 pt-12 text-sf-blue-700 lg:col-start-8 lg:pt-20">
              <div className="mb-3 text-sm font-medium uppercase">Recipe</div>
              <div className="flex flex-col gap-6">
                <h1 className="text-3xl uppercase">{title}</h1>
                <div className="text-sm">{description}</div>
                <div className="flex items-center gap-4 text-sm lg:gap-6">
                  {cookingTime && (
                    <>
                      <div className="inline-flex items-center font-medium">
                        <AlarmIcon className="mr-2  text-sf-blue-500" /> {cookingTime}
                      </div>
                    </>
                  )}
                  {cookingTime && serves && (
                    <span className="inline-block h-12 w-px bg-sf-blue-700"></span>
                  )}
                  {serves && (
                    <div className="inline-flex items-center font-medium">
                      <RegistrationIcon className="mr-2 text-sf-blue-500" /> {serves}
                    </div>
                  )}
                </div>
              </div>
              <button
                onClick={() => {
                  window.print();
                }}
                className="absolute bottom-6 right-6 inline-flex gap-2 rounded bg-sf-gray-200 px-3 py-2 text-sm font-medium print:hidden"
              >
                <PrintIcon className="text-sf-blue-500" />
                Print recipe
              </button>
            </div>
          </div>
        </Container>
      </div>
      <Container className="mb-18 sm:mb-32">
        <div className="grid grid-cols-4 gap-4 text-sf-blue-700 sm:grid-cols-6 md:gap-8 lg:gap-16 xl:gap-32 xl:px-28">
          <div className="col-span-4 flex flex-col gap-6 sm:col-span-2">
            <Description headline={`Ingredients`}>
              {renderRecipeRichText(ingredients.json, ingredients.links)}
            </Description>
          </div>
          <div className="col-span-4 sm:col-span-4">
            <Description headline={`Method`}>
              {renderRecipeRichText(method.json, method.links)}
            </Description>
          </div>
        </div>
      </Container>
      {relatedProduct && <RelatedProduct {...relatedProduct} />}
      {moreRecipes && moreRecipes.length > 0 && <RecipesCarousel recipes={moreRecipes} />}
    </>
  );
};

export default Recipe;
